import dayjs from "dayjs";
import type { InsiderTradingData } from "../hooks/useInsiderTrading";
import type { PriceRecordEod } from "../hooks/useCompanyPriceEod";

export type InsiderTradeKind = "purchase" | "sale" | "other";

/**
 * A row's `cashEquity` flag comes from the company route (the alerts' own
 * rule): only a cash equity security's P/S is a purchase or sale of stock, and
 * only its price values the row. A written call is filed as "S-Sale" at its
 * strike; here it is "other" and shows no dollar amount.
 */
type EquityAware = Pick<InsiderTradingData, "cashEquity">;

// Absent only on legacy per-ticker rows, which carry no instrument verdict.
const isCashEquity = (trade: EquityAware): boolean => trade.cashEquity ?? true;

export const getInsiderTradeKind = (
  transactionType: string,
  trade: EquityAware = {},
): InsiderTradeKind => {
  if (!isCashEquity(trade)) return "other";
  const code = transactionType.trim().split("-")[0].toUpperCase();
  if (code === "P") return "purchase";
  if (code === "S") return "sale";
  return "other";
};

export const getInsiderTradeLabel = (transactionType: string) => {
  const kind = getInsiderTradeKind(transactionType);
  if (kind === "purchase") return "Purchase";
  if (kind === "sale") return "Sale";
  const label = transactionType.split("-").slice(1).join("-").trim();
  if (label) return label;
  if (transactionType.trim()) return transactionType.trim();
  return "Other";
};

export const formatShareCount = (value: number) => {
  if (!Number.isFinite(value)) return "—";
  return value.toLocaleString("en-US", { maximumFractionDigits: 2 });
};

export const compactShareCount = (value: number) =>
  new Intl.NumberFormat("en-US", {
    notation: "compact",
    maximumFractionDigits: 1,
  }).format(value);

export const getFilingUrl = (url: string) => {
  try {
    const parsed = new URL(url);
    if (parsed.protocol !== "https:") return undefined;
    if (parsed.hostname === "sec.gov" || parsed.hostname.endsWith(".sec.gov"))
      return parsed.href;
  } catch {
    return undefined;
  }
  return undefined;
};

type InsiderTradeBar = {
  trade: InsiderTradingData;
  index: number;
  kind: "purchase" | "sale";
  x: number;
  shares: number;
  count: number;
  offset: number;
};

export const getInsiderActivity = (
  trades: InsiderTradingData[],
  prices: PriceRecordEod[],
  from: string,
  to: string,
) => {
  const start = dayjs(from).startOf("day");
  const end = dayjs(to).endOf("day");
  const inRange = (date: string) => {
    if (!date) return false;
    const value = dayjs(date);
    return value.isValid() && !value.isBefore(start) && !value.isAfter(end);
  };
  const filteredTrades = trades
    .filter((trade) => inRange(trade.transactionDate))
    .sort(
      (a, b) =>
        dayjs(b.transactionDate).valueOf() - dayjs(a.transactionDate).valueOf(),
    );
  const months = [];
  for (
    let month = start.startOf("month");
    !month.isAfter(end);
    month = month.add(1, "month")
  ) {
    const first = Math.max(month.valueOf(), start.valueOf());
    const last = Math.min(month.endOf("month").valueOf(), end.valueOf());
    months.push({
      key: month.format("YYYY-MM"),
      x: (first + last) / 2,
      first,
      last,
      bought: 0,
      sold: 0,
      count: 0,
    });
  }
  const byMonth = new Map(months.map((month) => [month.key, month]));
  let bought = 0;
  let sold = 0;
  const buyers = new Set<string>();
  const sellers = new Set<string>();
  filteredTrades.forEach((trade) => {
    const kind = getInsiderTradeKind(trade.transactionType, trade);
    const shares = Math.abs(trade.securitiesTransacted);
    if (!Number.isFinite(shares) || shares === 0 || kind === "other") return;
    const month = byMonth.get(dayjs(trade.transactionDate).format("YYYY-MM"));
    if (!month) return;
    month.count += 1;
    const identity = trade.reportingCik ?? trade.reportingName;
    if (kind === "purchase") {
      bought += shares;
      month.bought += shares;
      buyers.add(identity);
    } else {
      sold += shares;
      month.sold += shares;
      sellers.add(identity);
    }
  });
  const dailyGroups = new Map<string, InsiderTradeBar>();
  filteredTrades.forEach((trade, index) => {
    const kind = getInsiderTradeKind(trade.transactionType, trade);
    const shares = Math.abs(trade.securitiesTransacted);
    if (kind === "other" || !Number.isFinite(shares) || shares === 0) return;
    const date = dayjs(trade.transactionDate).format("YYYY-MM-DD");
    const key = `${date}-${kind}`;
    const group = dailyGroups.get(key);
    if (group) {
      group.shares += shares;
      group.count += 1;
      if (shares > Math.abs(group.trade.securitiesTransacted)) {
        group.trade = trade;
        group.index = index;
      }
      return;
    }
    dailyGroups.set(key, {
      trade,
      index,
      kind,
      x: dayjs(date).valueOf(),
      shares,
      count: 1,
      offset: 0,
    });
  });
  const dailyVolumes = new Map<number, number>();
  const tradeBars = Array.from(dailyGroups.values()).map((group) => {
    const offset = dailyVolumes.get(group.x) ?? 0;
    dailyVolumes.set(group.x, offset + group.shares);
    return { ...group, offset };
  });
  const pricePoints = prices
    .filter(
      (price) =>
        inRange(price.date) && Number.isFinite(price.close) && price.close > 0,
    )
    .sort((a, b) => dayjs(a.date).valueOf() - dayjs(b.date).valueOf())
    .map((price) => ({ x: dayjs(price.date).valueOf(), y: price.close }));

  return {
    trades: filteredTrades,
    months,
    pricePoints,
    tradeBars,
    bought,
    sold,
    buyers: buyers.size,
    sellers: sellers.size,
  };
};

export type InsiderActivity = ReturnType<typeof getInsiderActivity>;

export const INSIDER_BAR_WIDTH = 8;

export const getNotableInsiderBars = (
  bars: InsiderTradeBar[],
  from: number,
  to: number,
  plotWidth: number,
): InsiderTradeBar[] => {
  if (plotWidth <= 0 || to <= from) return [];
  const selected = new Set<InsiderTradeBar>();
  for (const kind of ["purchase", "sale"] as const) {
    const ranked = bars
      .filter((bar) => bar.kind === kind)
      .sort((a, b) =>
        a.shares === b.shares ? b.x - a.x : b.shares - a.shares,
      );
    const cutoff = ranked[Math.ceil(ranked.length / 4) - 1]?.shares;
    if (cutoff === undefined) continue;
    const selectedDates: number[] = [];
    for (const bar of ranked) {
      if (bar.shares < cutoff) break;
      const hasRoom = selectedDates.every(
        (date) =>
          (Math.abs(bar.x - date) / (to - from)) * plotWidth >=
          INSIDER_BAR_WIDTH + 12,
      );
      if (hasRoom) {
        selected.add(bar);
        selectedDates.push(bar.x);
      }
    }
  }
  return bars
    .filter((bar) => selected.has(bar))
    .map((bar) => ({ ...bar, offset: 0 }));
};

export const getInsiderBarPositions = (
  positions: { index: number; x: number }[],
) => {
  const sorted = [...positions].sort((a, b) => a.x - b.x);
  const result = new Map(sorted.map(({ index, x }) => [index, x]));
  const separation = INSIDER_BAR_WIDTH + 2;
  // Same-side bars are already spaced 20px apart, so collisions can only be
  // opposite-side pairs. Center the pair on its dates without dropping either.
  for (let i = 0; i < sorted.length - 1; i += 1) {
    const first = sorted[i];
    const second = sorted[i + 1];
    if (second.x - first.x >= separation) continue;
    const midpoint = (first.x + second.x) / 2;
    result.set(first.index, midpoint - separation / 2);
    result.set(second.index, midpoint + separation / 2);
    i += 1;
  }
  return result;
};

export interface InsiderTradePrice {
  price: number;
  estimatedFrom?: string;
}

/**
 * `estimateAwards` (default true): whether an unpriced stock award may be
 * valued from the viewed listing's close. False when that listing is an ADS —
 * its price is per depositary share, not per underlying share, so the
 * estimate would be off by the ADS ratio (TSMC: five). Reported prices are
 * always kept.
 */
export const getInsiderTradePrices = (
  trades: InsiderTradingData[],
  prices: PriceRecordEod[],
  options: { estimateAwards?: boolean } = {},
) => {
  const closes = new Map<string, number>();
  prices.forEach(({ date, close }) => {
    if (date && dayjs(date).isValid() && Number.isFinite(close) && close > 0)
      closes.set(dayjs(date).format("YYYY-MM-DD"), close);
  });
  const result = new Map<InsiderTradingData, InsiderTradePrice>();
  trades.forEach((trade) => {
    // An option's or note's `price` is a strike or conversion price, not a
    // share price: nothing values such a row, reported or estimated.
    if (!isCashEquity(trade)) return;
    if (Number.isFinite(trade.price) && trade.price > 0) {
      result.set(trade, { price: trade.price });
      return;
    }
    if (options.estimateAwards === false) return;
    const isAward =
      trade.transactionType.trim().split("-")[0].toUpperCase() === "A";
    // The underlying share price cannot value an option or warrant grant.
    if (!isAward || /\b(options?|warrants?)\b/i.test(trade.securityName))
      return;
    const transactionDate = dayjs(trade.transactionDate);
    if (!trade.transactionDate || !transactionDate.isValid()) return;
    for (let daysBack = 0; daysBack <= 7; daysBack += 1) {
      const date = transactionDate
        .subtract(daysBack, "day")
        .format("YYYY-MM-DD");
      const close = closes.get(date);
      if (close === undefined) continue;
      result.set(trade, { price: close, estimatedFrom: date });
      break;
    }
  });
  return result;
};

export type InsiderSortField =
  | "totalValue"
  | "shares"
  | "tradeDate"
  | "ownedAfter";
export type InsiderSortDirection = "asc" | "desc";

export const sortInsiderTransactions = (
  trades: InsiderTradingData[],
  valuations: Map<InsiderTradingData, InsiderTradePrice>,
  field: InsiderSortField,
  direction: InsiderSortDirection,
) => {
  const valueOf = (trade: InsiderTradingData) => {
    if (field === "shares") return Math.abs(trade.securitiesTransacted);
    if (field === "ownedAfter") return trade.securitiesOwned;
    if (field === "tradeDate")
      return trade.transactionDate
        ? dayjs(trade.transactionDate).valueOf()
        : NaN;
    const total =
      Math.abs(trade.securitiesTransacted) *
      (valuations.get(trade)?.price ?? NaN);
    return total > 0 ? total : NaN;
  };
  return trades
    .map((trade, index) => ({ trade, index, value: valueOf(trade) }))
    .sort((a, b) => {
      const aValid = Number.isFinite(a.value);
      const bValid = Number.isFinite(b.value);
      if (!aValid && bValid) return 1;
      if (aValid && !bValid) return -1;
      if (!aValid || a.value === b.value) return a.index - b.index;
      const comparison = a.value - b.value;
      return direction === "asc" ? comparison : -comparison;
    })
    .map(({ trade }) => trade);
};
